import { FC } from "preact/compat";
import { MasterDataTable } from "../../../components/DataTable/MasterDataTable";
import { useEffect, useState } from "react";
import { Label } from "../../../interfaces/Label";
import { TableColumn } from "react-data-table-component";
import { AddLabel } from "../../../components/Labels/AddLabel";
import useUser from "../../../hooks/User";
import { IconButton, Menu, MenuItem } from "@mui/material";
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { DeleteLabel } from "../../../components/Labels/DeleteLabel";
import { Modal } from "bootstrap";
import { UpdateLabel } from "../../../components/Labels/UpdateLabel";
import { useIntl } from "react-intl";
import useDynamicTab from "../../../hooks/useDynamicTab";
import { Agent } from "../../../interfaces/Agent";
import useModalEffect from "../../../hooks/useModalEffect";
import { AddAgent } from "../../../components/Agent/AddAgent";
import { UpdateAgent } from "../../../components/Agent/UpdateAgent";
import { DeleteAgent } from "../../../components/Agent/DeleteAgent";
import { useDataTableStore } from "../../../stores/DataTableStore";

const MasterAgents: FC = () => {
    const user = useUser()
    const intl = useIntl()

    const [agent, setAgent] = useState<Agent>({} as Agent);
    const [showUpdateModal, setShowUpdateModal] = useState<boolean>(false);
    const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
    const [openMenuId, setOpenMenuId] = useState<string | null>(null);
    const { tablesData } = useDataTableStore();

    const handleClick = (menuId: string) => {
        setOpenMenuId(menuId);
    };

    const handleClose = () => {
        setOpenMenuId(null);
    };

    const tableColumns: TableColumn<Agent>[] = [
        {
            name: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.ACTION' }),
            center: true,
            width: '70px',
            cell: (row) => {
                return (
                    <>
                        <IconButton
                            aria-label={`more-${row.id}`}
                            id={`menu-anchor-${row.id}`}
                            onClick={() => handleClick(row.id)}
                        >
                            <MoreVertIcon />
                        </IconButton>

                        <Menu
                            id={`menu-${row.id}`}
                            anchorEl={document.getElementById(`menu-anchor-${row.id}`)}
                            open={openMenuId === row.id}
                            onClose={handleClose}
                            disableScrollLock={true}
                        >
                            <MenuItem onClick={() => {
                                handleClose();
                                setShowUpdateModal(true);
                                setAgent(row);
                            }}>
                                <i className="fas fa-edit fs-4 me-4"></i>
                                <span>{intl.formatMessage({ id: 'FORM.ACTION.UPDATE' })}</span>
                            </MenuItem>

                            <MenuItem onClick={() => {
                                handleClose();
                                setShowDeleteModal(true);
                                setAgent(row);
                            }}>
                                <i className="fas fa-trash fs-3 me-5"></i>
                                <span>{intl.formatMessage({ id: 'FORM.ACTION.DELETE' })}</span>
                            </MenuItem>
                        </Menu>
                    </>
                )
            }
        },
        {
            name: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.NAME' }),
            selector: row => row.label,
            sortable: true,
        },
        {
            name: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.PHONE' }),
            selector: row => row.phone,
            sortable: true,
        },
        {
            name: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.AGENT' }),
            selector: row => row.agentInfo,
            sortable: true,
            wrap: true
        },
        {
            name: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.STATUS' }),
            sortable: true,
            cell: (row) => {
                if (row.connected) {
                    return (
                        <span className="badge badge-light-success fs-8 fw-bolder" >
                            <i className="fas fa-link me-2"></i> {intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.CONNECTED' })}
                        </span >
                    )
                } else {
                    return (
                        <span className="badge badge-light-danger fs-8 fw-bolder">
                            <i className="fas fa-unlink me-2"></i> {intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.DISCONNECTED' })}
                        </span>
                    )
                }
            }
        },
    ]

    const actions = [
        <AddAgent />
    ]

    useModalEffect({
        showModal: showUpdateModal,
        setShowModal: setShowUpdateModal,
        modalId: `update-agent-modal`,
    })

    useModalEffect({
        showModal: showDeleteModal,
        setShowModal: setShowDeleteModal,
        modalId: `delete-agent-modal`,
    })

    useDynamicTab({ title: `${intl.formatMessage({ id: 'MENU.AGENT' })} | CRM WooBlazz` });

    return (
        <>
            <ol className="breadcrumb text-muted fs-6 fw-bold mb-5">
                <li className="breadcrumb-item text-dark">{intl.formatMessage({ id: 'MENU.AGENT' })}</li>
            </ol>
            <MasterDataTable
                tableKey="agents"
                tableColumns={tableColumns}
                actions={actions}
                apiURL="chat-agent/admin/phones"
            />
            {
                showUpdateModal && <UpdateAgent agent={agent} />
            }
            {
                showDeleteModal && <DeleteAgent agent={agent!} />
            }
        </>
    )
}

export { MasterAgents }